window.Shopify = window.Shopify || {};

// Module-level guards
let __crossSellAdding = false; // prevents rapid double-submits across any button in the modal

/**
 * Opens the cross-sell modal using the native dialog element
 * @param {Object} cartData - Data from the cart add event
 */
function openCrossSellModal(cartData) {
  const modal = document.getElementById('CrossSellModal');
  const cartElement = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

  if (!modal || !cartData.handle) {
    if (cartElement) cartElement.renderContents(cartData);
    return;
  }

  const addedProductContainer = modal.querySelector('#CrossSell-AddedProduct');
  const recommendationsContainer = modal.querySelector('#CrossSell-Recommendations');

  // 1. Update added product info using DOM properties (no innerHTML)
  if (addedProductContainer) {
    const img = addedProductContainer.querySelector('.added-product-image');
    const name = addedProductContainer.querySelector('.product-name');
    const price = addedProductContainer.querySelector('.product-price');

    if (img) {
      img.src = cartData.image || '';
      img.alt = cartData.product_title || '';
    }
    if (name) name.textContent = cartData.product_title;
    if (price) {
      const format = window.money_format || Shopify.money_format || "KSh {{amount}}";
      try {
        price.textContent = typeof Shopify.formatMoney === 'function'
          ? Shopify.formatMoney(cartData.price, format)
          : (cartData.price / 100).toFixed(2);
      } catch (e) {
        console.warn('Shopify.formatMoney failed, using fallback', e);
        price.textContent = (cartData.price / 100).toFixed(2);
      }
    }
    addedProductContainer.hidden = false;
  }

  // 2. Fetch cross-sell products using Section Rendering API (with modal_mode)
  const productUrl = `${window.Shopify.routes.root || '/'}products/${cartData.handle}?sections=cross-sell-products&modal_mode=true`;

  fetch(productUrl)
    .then(response => {
      if (!response.ok) throw new Error('Network response not ok');
      return response.json();
    })
    .then(sections => {
      const html = sections['cross-sell-products'];

      // Use DOMParser to safely handle the fetched HTML string
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const content = doc.querySelector('.cross-sell-products');

      if (content && content.children.length > 0) {
        content.setAttribute('data-modal-mode', 'true');
        recommendationsContainer.replaceChildren(content);
        modal.showModal();
        document.body.classList.add('cross-sell-modal-open');

        initModalVariantSelectors();

        // wire add-to-cart buttons
        modal.querySelectorAll('.modal-add-to-cart').forEach(btn => {
          btn.addEventListener('click', () => {
            const variantId = btn.dataset.variantId;
            if (!variantId) return;

            // Lock: block rapid double-submits from this or any other button in the modal
            if (__crossSellAdding) return;
            __crossSellAdding = true;

            btn.disabled = true;
            btn.querySelector('.button-text').classList.add('hidden');
            btn.querySelector('.loading__spinner').classList.remove('hidden');

            fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify({ id: variantId, quantity: 1 })
            })
              .then(response => {
                if (!response.ok) {
                  console.error('Cross-sell add failed:', response.status);
                  btn.disabled = false;
                  btn.querySelector('.button-text').classList.remove('hidden');
                  btn.querySelector('.loading__spinner').classList.add('hidden');
                  __crossSellAdding = false;
                  return;
                }
                return response.json();
              })
              .then(response => {
                if (response) {
                  window.__pendingCartResponse = response;
                  closeCrossSellModal(btn, true);
                  // no need to reset __crossSellAdding here — modal is closing/redirecting
                }
              })
              .catch(err => {
                console.error(err);
                btn.disabled = false;
                btn.querySelector('.button-text').classList.remove('hidden');
                btn.querySelector('.loading__spinner').classList.add('hidden');
                __crossSellAdding = false;
              });
          }, { once: true });
        });

      } else {
        // Fallback if no cross-sells
        if (cartElement) cartElement.renderContents(cartData);
      }
    })
    .catch(error => {
      console.error('Error fetching cross-sells:', error);
      if (cartElement) cartElement.renderContents(cartData);
    });

  // Handle backdrop click to close (desktop)
  modal.onclick = (e) => {
    if (e.target === modal) {
      closeCrossSellModal();
    }
  };

  // Mobile: close on backdrop click
  if (window.innerWidth <= 480) {
    document.querySelector('.cross-sell-modal + .cross-sell-modal\\:\\:backdrop')?.addEventListener('click', closeCrossSellModal);
  }

  // NOTE: the modal 'change' listener used to be attached here, on every call
  // to openCrossSellModal — since `modal` is a persistent DOM node, that meant
  // one extra stacked listener per cart-add event. It's now attached once,
  // outside this function (see initCrossSellChangeHandler below).
}

/**
 * Attaches the variant-change sync handler to the modal exactly once.
 * Call this at script load time, not inside openCrossSellModal.
 */
function initCrossSellChangeHandler() {
  const modal = document.getElementById('CrossSellModal');
  if (!modal || modal.dataset.changeHandlerAttached === 'true') return;

  modal.addEventListener('change', (e) => {
    const select = e.target.closest('.variant-select');
    if (!select) return;

    const productId = select.dataset.productId;
    const newVariantId = select.value;

    // sync button
    const btn = modal.querySelector(`.modal-add-to-cart[data-product-id="${productId}"]`);
    if (btn) btn.dataset.variantId = newVariantId;

    // sync hidden input
    const input = modal.querySelector(`.variant-id-input[data-product-id="${productId}"]`);
    if (input) input.value = newVariantId;

    // sync price
    const selectedOption = select.options[select.selectedIndex];
    const price = modal.querySelector(`.cross-sell__price[data-product-id="${productId}"] .price`);
    if (price) price.textContent = selectedOption.dataset.price;
  });

  modal.dataset.changeHandlerAttached = 'true';
}

// Attach the change handler once, as soon as this script runs.
document.addEventListener('DOMContentLoaded', initCrossSellChangeHandler);
// Fallback in case DOMContentLoaded already fired before this script loaded.
if (document.readyState !== 'loading') initCrossSellChangeHandler();

/**
 * Closes the cross-sell modal
 * @param {HTMLElement|null} clickedBtn - The button that triggered the close
 */
function closeCrossSellModal(clickedBtn = null, redirect= true) {
  const modal = document.getElementById('CrossSellModal');
  if (!modal) return;

  const btn = clickedBtn || modal.querySelector('.cross-sell__button');
  if (btn) {
    const btnText = btn.querySelector('.button-text');
    if (btnText) btnText.textContent = 'Added to Cart';
    btn.querySelector('.loading__spinner')?.classList.add('hidden');
    btn.disabled = true;
  }

  redirect ? window.location.href = window.routes.cart_url : null
  setTimeout(() => {
    modal.close();
    document.body.classList.remove('cross-sell-modal-open');
    __crossSellAdding = false; // reset lock once modal cycle is done
  }, 2000);
}

/**
 * SIMULATE CROSS-SELL POPUP (FOR TESTING)
 * Run this in Browser Console: testCrossSellModal()
 */
function testCrossSellModal() {
  const dummyCartData = {
    handle: "air-optix-aqua-hydraglyde",
    product_title: "Air Optix Monthly Contact Lenses",
    image: "https://optica.africa/cdn/shop/products/contactsContactlenses2023091414570241214.jpg?v=1757746965&width=600",
    price: 1500000
  };

  openCrossSellModal(dummyCartData);
}

/**
 * Helper to ensure Shopify.formatMoney is available
 */
if (typeof Shopify.formatMoney !== 'function') {
  Shopify.formatMoney = function(cents, format) {
    if (typeof cents == 'string') { cents = cents.replace('.',''); }
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || "KSh {{amount}}";

    function defaultOption(opt, defaultValue) {
      return (typeof opt == 'undefined' ? defaultValue : opt);
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal   = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) { return 0; }

      number = (number/100.0).toFixed(precision);

      var parts   = number.split('.'),
          dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
          cents   = parts[1] ? (decimal + parts[1]) : '';

      return dollars + cents;
    }

    if (!formatString.match(placeholderRegex)) return (cents/100).toFixed(2);

    switch(formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  };
}

/**
 * Initialize variant selectors in the modal
 */
function initModalVariantSelectors() {
  document.querySelectorAll('.cross-sell-products[data-modal-mode="true"] .variant-select').forEach(function(select) {
    select.addEventListener('change', function() {
      const productItem = this.closest('.cross-sell__item');
      if (!productItem) return;

      const priceDisplay = productItem.querySelector('.price');
      const addButton = productItem.querySelector('.modal-add-to-cart');
      const variantInput = productItem.querySelector('.variant-id-input');

      const selectedOption = this.options[this.selectedIndex];

      if (addButton) addButton.dataset.variantId = this.value;
      if (variantInput) variantInput.value = this.value;
      if (priceDisplay && selectedOption && selectedOption.dataset.price) {
        priceDisplay.textContent = selectedOption.dataset.price;
      }
    });
  });
}